import { createContext, useContext } from 'react';
import { PublicIslandJourney } from '../pages/public/types';
import { AnalyticsInfo } from '../types/analytics';
import {
  Contract,
  ContractAssignedVessels,
  ContractListItem,
  NewContract,
  SimpleContractAssignedVessels,
} from '../types/contract';
import { AppData } from '../types/core';
import {
  CrewMember,
  CrewMemberCertificate,
  CrewMemberListItem,
  NewCrewMember,
  NewCrewMemberCertificate,
  UpdatedCrewMember,
} from '../types/crew';
import {
  AdminDashboardInfo,
  AdminDashboarPendingInvoiceElement,
  OperatorDashboardInfo,
} from '../types/dashboard';
import {
  ContractInsurance,
  NewContractInsurance,
  NewVesselInsurance,
  VesselInsurance,
} from '../types/insurance';
import {
  CreateInvoiceResponse,
  Invoice,
  InvoiceJourney,
  InvoiceListItem,
  InvoiceRejectionNote,
  InvoiceValidationErrors,
  NewInvoice,
} from '../types/invoices';
import {
  Island,
  NewIsland,
  PublicIsland,
  UpdatedIsland,
} from '../types/island';
import {
  Journey,
  JourneyChangePayload,
  JourneyChangeResponse,
  JourneyLog,
  JourneyPlaceholder,
  NewJourney,
  UpdatedJourneyLog,
} from '../types/journey';
import {
  ChangeOperatorStatusPayload,
  NewOperator,
  Operator,
} from '../types/operator';
import { NewPort, Port, UpdatedPort } from '../types/port';
import { Route, SimpleNewRoute, SimpleRoute } from '../types/route';
import { NewUser, User, UserOperator } from '../types/user';
import {
  NewVessel,
  NewVesselCertificate,
  UpdatedVessel,
  Vessel,
  VesselCertificate,
  VesselContractAssignment,
  VesselListItem,
} from '../types/vessel';
import { JourneyReasonNote } from '../pages/admin/Journeys/types';

export interface IotsApiContextValue {
  getUsers: () => Promise<User[]>;
  getUserById: (userId: string) => Promise<User>;
  createUser: (user: NewUser) => Promise<string>;
  updateUser: (updatedUser: User) => Promise<void>;
  updateUserState: (id: string, newIsActive: boolean) => Promise<void>;
  resendEmailInvitation: (user: User) => Promise<void>;
  createOperator: (operator: NewOperator) => Promise<string>;
  updateOperator: (updatedOperator: Operator) => Promise<void>;
  getOperators: () => Promise<Operator[]>;
  getOperatorById: (operatorId: string) => Promise<Operator>;
  updateOperatorState: (operator: ChangeOperatorStatusPayload) => Promise<void>;
  createContract: (contract: NewContract) => Promise<string>;
  deleteContract: (contractId: string) => Promise<void>;
  rescindContract: (contract: string) => Promise<Contract>;
  getContracts: () => Promise<ContractListItem[]>;
  getContractById: (contractId: string) => Promise<Contract>;
  updateContract: (contract: NewContract & { id: string }) => Promise<void>;
  getContractRoutes: (contractId: string) => Promise<Route[]>;
  createContractRoute: (
    contractId: string,
    newRoute: SimpleNewRoute,
  ) => Promise<SimpleRoute>;
  getContractRouteDeletionImpactedJourneys: (
    contractId: string,
    routeId: string,
  ) => Promise<number>;
  deleteContractRoute: (contractId: string, routeId: string) => Promise<void>;
  rescindContractRoute: (contractId: string, routeId: string) => Promise<void>;
  getContractAssignedVessels: (
    contractId: string,
  ) => Promise<ContractAssignedVessels | null>;
  updateContractAssignedVessels: (
    contractId: string,
    newAssignedVessels: SimpleContractAssignedVessels,
  ) => Promise<void>;
  createIsland: (island: NewIsland) => Promise<string>;
  updateIsland: (
    islandId: string,
    updatedIsland: UpdatedIsland,
  ) => Promise<void>;
  updateIslandVisibility: (islandId: string) => Promise<void>;
  getIslands: () => Promise<Island[]>;
  getIslandById: (id: string) => Promise<Island>;
  createPort: (port: NewPort) => Promise<string>;
  updatePort: (portId: string, port: UpdatedPort) => Promise<void>;
  deletePort: (portId: string) => Promise<void>;
  getPorts: () => Promise<Port[]>;
  getVessels: () => Promise<VesselListItem[]>;
  getVesselById: (vesselId: string) => Promise<Vessel>;
  createVessel: (vessel: NewVessel) => Promise<string>;
  updateVessel: (vessel: UpdatedVessel) => Promise<void>;
  deleteVessel: (vesselId: string) => Promise<void>;
  getVesselCertificates: (vesselId: string) => Promise<VesselCertificate[]>;
  addVesselCertificate: (
    vesselId: string,
    certificate: NewVesselCertificate,
  ) => Promise<VesselCertificate>;
  updateVesselCertificate: (
    vesselId: string,
    certificate: NewVesselCertificate & { id: string },
  ) => Promise<void>;
  approveVesselCertificate: (
    vesselId: string,
    certificateId: string,
  ) => Promise<VesselCertificate>;
  rejectVesselCertificate: (
    vesselId: string,
    certificateId: string,
    note: string,
  ) => Promise<VesselCertificate>;
  deleteVesselCertificate: (
    vesselId: string,
    certificateId: string,
  ) => Promise<void>;
  getVesselContractAssignments: (
    vesselId: string,
  ) => Promise<VesselContractAssignment[]>;
  getUserOperator: () => Promise<UserOperator>;
  getAppData: () => Promise<AppData>;
  getFile: (url: string) => Promise<File>;
  getCrewMembers: () => Promise<CrewMemberListItem[]>;
  createCrewMember: (crew: NewCrewMember) => Promise<string>;
  updateCrewMember: (crew: UpdatedCrewMember) => Promise<void>;
  deleteCrewMember: (crewMemberId: string) => Promise<void>;
  getCrewMemberById: (crewMemberId: string) => Promise<CrewMember>;
  getCrewMemberCertificates: (
    crewMemberId: string,
  ) => Promise<CrewMemberCertificate[]>;
  addCrewMemberCertificate: (
    crewMemberId: string,
    certificate: NewCrewMemberCertificate,
  ) => Promise<CrewMemberCertificate>;
  approveCrewMemberCertificate: (
    crewMemberId: string,
    certificateId: string,
  ) => Promise<CrewMemberCertificate>;
  rejectCrewMemberCertificate: (
    crewMemberId: string,
    certificateId: string,
    note: string,
  ) => Promise<CrewMemberCertificate>;
  deleteCrewMemberCertificate: (
    crewMemberId: string,
    certificateId: string,
  ) => Promise<void>;

  createJourney: (journey: NewJourney) => Promise<string>;
  updateJourneyLog: (
    updatedJourneyLog: UpdatedJourneyLog,
  ) => Promise<JourneyLog>;
  getJourneyById: (journeyId: string) => Promise<Journey>;
  getJourneyLog: (journeyId: string) => Promise<JourneyLog>;
  rescheduleJourney: (
    journeyChangePayload: JourneyChangePayload,
  ) => Promise<JourneyChangeResponse>;
  cancelJourney: (
    journeyChangePayload: JourneyChangePayload,
  ) => Promise<JourneyChangeResponse>;
  deleteAdhocJourney: (journeyId: string) => Promise<void>;
  submitJourney: (journeyId: string) => Promise<Journey | false>;
  unsubmitJourney: (journeyId: string) => Promise<Journey>;
  approveJourney: (journeyId: string) => Promise<Journey>;
  rejectJourney: (journeyId: string, note: string) => Promise<Journey>;
  getJourneyPlaceholders: (
    startDate: string,
    endDate: string,
    contractId: string,
  ) => Promise<JourneyPlaceholder[]>;
  getJourneyNotes: (journeyId: string) => Promise<JourneyReasonNote[]>;
  getInvoices: () => Promise<InvoiceListItem[]>;
  getInvoiceJourneysByContractId: (
    contractId: string,
    year: number,
    month: number,
  ) => Promise<InvoiceJourney[]>;
  validateInvoice: (
    contractId: string,
    year: number,
    month: number,
  ) => Promise<InvoiceValidationErrors>;
  createInvoice: (invoice: NewInvoice) => Promise<CreateInvoiceResponse>;
  getInvoiceById: (invoiceId: string) => Promise<Invoice>;
  getInvoiceJourneysByInvoiceId: (
    invoiceId: string,
  ) => Promise<InvoiceJourney[]>;
  approveInvoice: (invoiceId: string) => Promise<Invoice>;
  rejectInvoice: (invoiceId: string, note: string) => Promise<Invoice>;
  getInvoiceRejectionNotes: (
    invoiceId: string,
  ) => Promise<InvoiceRejectionNote[]>;
  getPublicIslands: () => Promise<PublicIsland[]>;
  getPublicJourneysByIslandId: (
    islandId: string,
  ) => Promise<PublicIslandJourney[]>;
  getOperatorDashboardInfo: () => Promise<OperatorDashboardInfo>;
  getAdminDashboardInfo: () => Promise<AdminDashboardInfo>;
  getAdminDashboardPendingInvoiceInfo: () => Promise<AdminDashboarPendingInvoiceElement>;
  getYearAnalytics: (year: number) => Promise<AnalyticsInfo>;
  getYearAnalyticsCsvFile: (year: number) => Promise<File>;
  requestAnalyticsRecalculation: () => Promise<void>;
  getVesselInsurances: (vesselId: string) => Promise<VesselInsurance[]>;
  addVesselInsurance: (
    vesselId: string,
    insurance: NewVesselInsurance,
  ) => Promise<VesselInsurance>;
  deleteVesselInsurance: (
    vesselId: string,
    insuranceId: string,
  ) => Promise<void>;
  getContractInsurances: (contractId: string) => Promise<ContractInsurance[]>;
  addContractInsurance: (
    contractId: string,
    insurance: NewContractInsurance,
  ) => Promise<ContractInsurance>;
  deleteContractInsurance: (
    contractId: string,
    insuranceId: string,
  ) => Promise<void>;
}

export const defaultValue: IotsApiContextValue = {
  getUsers: () => Promise.reject(Error('Not implemented')),
  getUserById: () => Promise.reject(Error('Not implemented')),
  createUser: () => Promise.reject(Error('Not implemented')),
  updateUser: () => Promise.reject(Error('Not implemented')),
  updateUserState: () => Promise.reject(Error('Not implemented')),
  resendEmailInvitation: () => Promise.reject(Error('Not implemented')),
  createOperator: () => Promise.reject(Error('Not implemented')),
  updateOperator: () => Promise.reject(Error('Not implemented')),
  getOperators: () => Promise.reject(Error('Not implemented')),
  getOperatorById: () => Promise.reject(Error('Not implemented')),
  updateOperatorState: () => Promise.reject(Error('Not implemented')),
  createContract: () => Promise.reject(Error('Not implemented')),
  deleteContract: () => Promise.reject(Error('Not implemented')),
  rescindContract: () => Promise.reject(Error('Not implemented')),
  createIsland: () => Promise.reject(Error('Not implemented')),
  updateIsland: () => Promise.reject(Error('Not implemented')),
  updateIslandVisibility: () => Promise.reject(Error('Not implemented')),
  getIslands: () => Promise.reject(Error('Not implemented')),
  getIslandById: () => Promise.reject(Error('Not implemented')),
  getContracts: () => Promise.reject(Error('Not implemented')),
  getContractById: () => Promise.reject(Error('Not implemented')),
  updateContract: () => Promise.reject(Error('Not implemented')),
  getContractRoutes: () => Promise.reject(Error('Not implemented')),
  createContractRoute: () => Promise.reject(Error('Not implemented')),
  getContractRouteDeletionImpactedJourneys: () =>
    Promise.reject(Error('Not implemented')),
  deleteContractRoute: () => Promise.reject(Error('Not implemented')),
  rescindContractRoute: () => Promise.reject(Error('Not implemented')),
  getContractAssignedVessels: () => Promise.reject(Error('Not implemented')),
  updateContractAssignedVessels: () => Promise.reject(Error('Not implemented')),
  createPort: () => Promise.reject(Error('Not implemented')),
  updatePort: () => Promise.reject(Error('Not implemented')),
  deletePort: () => Promise.reject(Error('Not implemented')),
  getPorts: () => Promise.reject(Error('Not implemented')),
  getVessels: () => Promise.reject(Error('Not implemented')),
  getVesselById: () => Promise.reject(Error('Not implemented')),
  createVessel: () => Promise.reject(Error('Not implemented')),
  updateVessel: () => Promise.reject(Error('Not implemented')),
  deleteVessel: () => Promise.reject(Error('Not implemented')),
  getVesselCertificates: () => Promise.reject(Error('Not implemented')),
  addVesselCertificate: () => Promise.reject(Error('Not implemented')),
  updateVesselCertificate: () => Promise.reject(Error('Not implemented')),
  approveVesselCertificate: () => Promise.reject(Error('Not implemented')),
  rejectVesselCertificate: () => Promise.reject(Error('Not implemented')),
  deleteVesselCertificate: () => Promise.reject(Error('Not implemented')),
  getVesselContractAssignments: () => Promise.reject(Error('Not implemented')),
  getUserOperator: () => Promise.reject(Error('Not implemented')),
  getAppData: () => Promise.reject(Error('Not implemented')),
  getFile: () => Promise.reject(Error('Not implemented')),
  getCrewMembers: () => Promise.reject(Error('Not implemented')),
  createCrewMember: () => Promise.reject(Error('Not implemented')),
  updateCrewMember: () => Promise.reject(Error('Not implemented')),
  deleteCrewMember: () => Promise.reject(Error('Not implemented')),
  getCrewMemberById: () => Promise.reject(Error('Not implemented')),
  getCrewMemberCertificates: () => Promise.reject(Error('Not implemented')),
  addCrewMemberCertificate: () => Promise.reject(Error('Not implemented')),
  approveCrewMemberCertificate: () => Promise.reject(Error('Not implemented')),
  rejectCrewMemberCertificate: () => Promise.reject(Error('Not implemented')),
  deleteCrewMemberCertificate: () => Promise.reject(Error('Not implemented')),
  createJourney: () => Promise.reject(Error('Not implemented')),
  getJourneyById: () => Promise.reject(Error('Not implemented')),
  rescheduleJourney: () => Promise.reject(Error('Not implemented')),
  cancelJourney: () => Promise.reject(Error('Not implemented')),
  deleteAdhocJourney: () => Promise.reject(Error('Not implemented')),
  submitJourney: () => Promise.reject(Error('Not implemented')),
  unsubmitJourney: () => Promise.reject(Error('Not implemented')),
  approveJourney: () => Promise.reject(Error('Not implemented')),
  rejectJourney: () => Promise.reject(Error('Not implemented')),
  getJourneyLog: () => Promise.reject(Error('Not implemented')),
  updateJourneyLog: () => Promise.reject(Error('Not implemented')),
  getJourneyPlaceholders: () => Promise.reject(Error('Not implemented')),
  getJourneyNotes: () => Promise.reject(Error('Not implemented')),
  getInvoices: () => Promise.reject(Error('Not implemented')),
  getInvoiceJourneysByContractId: () =>
    Promise.reject(Error('Not implemented')),
  validateInvoice: () => Promise.reject(Error('Not implemented')),
  createInvoice: () => Promise.reject(Error('Not implemented')),
  getInvoiceById: () => Promise.reject(Error('Not implemented')),
  getInvoiceJourneysByInvoiceId: () => Promise.reject(Error('Not implemented')),
  approveInvoice: () => Promise.reject(Error('Not implemented')),
  rejectInvoice: () => Promise.reject(Error('Not implemented')),
  getInvoiceRejectionNotes: () => Promise.reject(Error('Not implemented')),
  getPublicIslands: () => Promise.reject(Error('Not implemented')),
  getPublicJourneysByIslandId: () => Promise.reject(Error('Not implemented')),
  getOperatorDashboardInfo: () => Promise.reject(Error('Not implemented')),
  getAdminDashboardInfo: () => Promise.reject(Error('Not implemented')),
  getAdminDashboardPendingInvoiceInfo: () =>
    Promise.reject(Error('Not implemented')),
  getYearAnalytics: () => Promise.reject(Error('Not implemented')),
  getYearAnalyticsCsvFile: () => Promise.reject(Error('Not implemented')),
  requestAnalyticsRecalculation: () => Promise.reject(Error('Not implemented')),
  getVesselInsurances: () => Promise.reject(Error('Not implemented')),
  addVesselInsurance: () => Promise.reject(Error('Not implemented')),
  deleteVesselInsurance: () => Promise.reject(Error('Not implemented')),
  getContractInsurances: () => Promise.reject(Error('Not implemented')),
  addContractInsurance: () => Promise.reject(Error('Not implemented')),
  deleteContractInsurance: () => Promise.reject(Error('Not implemented')),
};

/**
 * Intead of letting the components and pages to know about the implementation of the network requests and
 * possible processing of the received data, we provide a context with the methods to obtain the appropriate
 * data in the expected format.
 * With this, the actual implementation of the data layer can be changed without the components & pages needing
 * to be modified.
 */
const IotsApiContext = createContext<IotsApiContextValue>(defaultValue);

export default IotsApiContext;

export const useIotsApi = () => useContext(IotsApiContext);
